import { trigger, state, style, transition, animate } from '@angular/animations';
import { Component, ElementRef, ViewChild, AfterViewInit, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ApiService } from 'src/app/services/api/api.service';
import { TabControlService } from 'src/app/services/tab-control.service';
import { environment } from 'src/environments/environment';
import * as Constants from "src/app/constants/constants";
import { NotificationService } from 'src/app/services/notification.service';

@Component({
  selector: 'app-signature',
  templateUrl: './signature.component.html',
  styleUrls: ['./signature.component.scss'],
    animations: [
      trigger('fadeIn', [
        state('void', style({ opacity: 0 })),
        transition(':enter', [
          animate('1000ms ease-in-out', style({ opacity: 1 })),
        ]),
      ]),
    ]
})
export class SignatureComponent implements AfterViewInit, OnInit {
  @ViewChild('signaturePad', { static: false }) signaturePadElement!: ElementRef<HTMLCanvasElement>;
  @Output() signatureSaved = new EventEmitter<string>();

  @ViewChild('ngOtpInput', { static: false}) ngOtpInput: any;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private drawing = false;

  enterOTPText: string = 'Verify Your Signature';
  timerText: string = ' seconds left';
  resendOTPText: string = 'Resend One Time Password';
  resendIcon: string = "replay";
  submitText: string = 'Verify & Sign';

  defaultOTPMessage: string = 'Once you have signed above, please enter the One Time Password (OTP) sent to your email and phone, then click the \'Verify & Sign\' Button';
  OTPMessage: string = this.defaultOTPMessage;


  otpReady: boolean = false;
  otpValue: number = 0;
  OTPValid: boolean = false;

  isLoading: boolean = false;
  requestingOTP : boolean = false;
  // OTP VALUE
  countdown: number = 120;
  countdownIntervalId: any;

  DefaultOTPRequestStats: any = {time: null, phone: null};
  OTPRequestStats: any = this.DefaultOTPRequestStats;

  otpForm: FormGroup;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly tabControlService: TabControlService,
    private readonly apiService: ApiService,
    private readonly notificationService: NotificationService,
  ) { 
    this.otpForm = this.formBuilder.group({
      otp: ['']
    });
  }

  ngOnInit(): void {
    if (this.region === 1) { // Diaspora
      this.OTPMessage = 'Once you have signed above, please enter the One Time Password (OTP) sent to your email, then click the \'Verify & Sign\' Button';
    }
    // Update time countdown to continue if exists
    if (this.OTPRequestStats.time !== null) {
      let timeDiff = Date.now() / 1000 - this.OTPRequestStats.time;
      // console.log('Time Diff ! ', timeDiff);

      if (timeDiff > 0) {
        this.countdown = Number.parseInt((120-timeDiff).toString());
      }
    }

    this.startOrRestartCountdown();
  }

  ngAfterViewInit(): void {
    this.canvas = this.signaturePadElement.nativeElement;
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })!;
    this.resizeCanvas();

    // Event listeners for drawing
    this.canvas.addEventListener('mousedown', this.startDrawing.bind(this));
    this.canvas.addEventListener('mousemove', this.draw.bind(this));
    this.canvas.addEventListener('mouseup', this.stopDrawing.bind(this));
    this.canvas.addEventListener('mouseout', this.stopDrawing.bind(this));

    // Event listeners for touch devices
    this.canvas.addEventListener('touchstart', this.startDrawing.bind(this));
    this.canvas.addEventListener('touchmove', this.draw.bind(this));
    this.canvas.addEventListener('touchend', this.stopDrawing.bind(this));
    this.sendOTP(true);
  }

  get region(): any {
    return this.tabControlService.regionType;
  }

  private resizeCanvas(): void {
    const ratio = window.devicePixelRatio || 1;
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;
    this.canvas.width = width * ratio;
    this.canvas.height = height * ratio;
    this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';
    this.ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset any existing transforms
    this.ctx.scale(ratio, ratio);
  }

  private startDrawing(event: MouseEvent | TouchEvent): void {
    this.drawing = true;
    this.ctx.beginPath();
    this.ctx.moveTo(this.getX(event), this.getY(event));
  }

  private draw(event: MouseEvent | TouchEvent): void {
    if (!this.drawing) return;
    this.ctx.lineTo(this.getX(event), this.getY(event));
    this.ctx.stroke();
  }

  private stopDrawing(): void {
    this.drawing = false;
  }

  private getX(event: MouseEvent | TouchEvent): number {
    if (event instanceof MouseEvent) {
      return event.offsetX;
    } else {
      return event.touches[0].clientX - this.canvas.getBoundingClientRect().left;
    }
  }

  private getY(event: MouseEvent | TouchEvent): number {
    if (event instanceof MouseEvent) {
      return event.offsetY;
    } else {
      return event.touches[0].clientY - this.canvas.getBoundingClientRect().top;
    }
  }

  isCanvasBlank(): boolean {
    // Ensure the context is initialized
    if (!this.ctx) {
      return true; // Assume the canvas is blank if the context is not initialized
    }
  
    // Get the pixel data from the canvas
    const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
  
    // Check if all pixels are transparent (or blank)
    for (let i = 3; i < imageData.length; i += 4) {
      if (imageData[i] !== 0) {
        // If any pixel is not fully transparent, the canvas is not blank
        return false;
      }
    }
  
    // If all pixels are transparent, the canvas is blank
    return true;
  }

  clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  save(): void {
    const image = this.canvas.toDataURL('image/png');
    this.signatureSaved.emit(image);
    this.notificationService.closeAllModals();
  }

  // OTP Functions

  startOrRestartCountdown(): void {
    // Stop the current countdown if it exists
    this.stopCountdown();
  
    // Start a new countdown
    this.countdownIntervalId = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.stopCountdown(); // Stop the countdown when it reaches 0 or less
        this.OTPValid = false;
        this.OTPRequestStats = this.DefaultOTPRequestStats;
      }
    }, 1000);
  }

  stopCountdown() {
    clearInterval(this.countdownIntervalId);
  }

  onOtpChange(otp: string) {
    if (otp.length === 6) {
      this.otpReady = true;
      this.otpValue = Number.parseInt(otp);
      // console.log('OTP READY !!!', this.otpReady);
    } else {
      this.otpReady = false;
    }
  }

  async sendOTP(requestNow?: boolean): Promise<void> {
    if (!this.isLoading && (this.countdown <= 10 || requestNow)) {
      if (this.region === 1) { // Diaspora
        this.notificationService.viewToast(
          'info',
          `Sending OTP To Primary Settlor's Email.`,
          'This is the email you used in the 1st Step');
      } else {
        this.notificationService.viewToast(
          'info',
          `Sending OTP To Primary Settlor's Number.`,
          'This is the phone number you used in the 1st Step');
      }

      this.requestingOTP = true;
      this.ngOtpInput.setValue('');
      this.resendIcon = 'sync';
      let data;
      try {
        data = this.tabControlService.basicData;
    
        const response = await this.apiService.postRequest(
          environment.baseUrl + Constants.otpRequestURL, data);
    
        if (response.Status === 1) {
          // update the time OTP was requested:
          const timeStats = {time: Date.now() / 1000, phone: data.Phone};
          this.OTPRequestStats = timeStats;
          this.OTPValid = true;
          this.countdown = 120;

          this.startOrRestartCountdown();

          if (this.region === 1) { // Diaspora
            this.notificationService.viewToast(
              'info',
              `OTP sent to ${data.Email}.`);
          } else {
            this.notificationService.viewToast(
              'success',
              `OTP sent to ${data.Phone}.`,
              'We have also sent it to the Primary Settlor\'s Email');
          }

        } else {
          this.notificationService.viewToast('error', response.Message);
        }

      } catch (error) {
        console.log(':: Error !! ', error);
        this.notificationService.viewToast('error', 'Failed to Send OTP', 'An unexpected error occurred. Please try again');
      }
      this.requestingOTP = false;
      this.resendIcon = 'replay';
    }  
  }
  async verifyOtp(): Promise<void> {
    if (this.isCanvasBlank() || !this.otpReady) {
      if (this.isCanvasBlank()) this.notificationService.viewToast('error', 'Please sign above before proceeding');
      if (!this.otpReady) this.notificationService.viewToast('error', 'The OTP is Requred');
    } else {
      this.isLoading = true;
      this.notificationService.viewToast('info', `Please wait as we Veify the OTP`);

      try {
        let otpData = this.tabControlService.basicData;

        const data = {
          OTP: this.otpValue,
          Phone: otpData.Phone
        };

        const response = await this.apiService.postRequest(
          environment.baseUrl + Constants.otpValidateURL, data);

        if (response.Status === 1) {
          this.notificationService.viewToast('success', 'Signature captured', response.Message);

          // Reset OTP Trackers
          this.OTPValid = false;
          this.OTPRequestStats = this.DefaultOTPRequestStats;

          this.save();
        } else {
          this.notificationService.viewToast('error', response.Message);
          // this.emitOTPStatusToParent(false);
        }
      } catch (error) {
        console.error(':: Verify Signature Otp !! ', error);
        this.notificationService.viewToast('error', 'An unexpected error occurred. Please try again');
      }
      this.isLoading = false;
    }
  }

}
